// Spanish content for smartQ marketing site
import { SiteContent } from './content'

export const contentEs: SiteContent = {
  pages: {
    home: {
      title: "Inicio / Vista General",
      purpose: "Explicar de un vistazo por qué smartQ es importante para una pequeña empresa: menos espera visible, clientes más tranquilos y un servicio más fluido gracias a una cola virtual.",
      hero: {
        headline: "La cola en tu móvil, ¡no pierdas tu tiempo esperando!",
        sub: "Libérate de la gestión de la espera y suma más clientes... ¡encantados de volver! 😊<br><br>**Utilización fácil · Configuración simple · Sin compromiso de permanencia**",
        ctaPrimary: "Prueba gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "**Más clientes**",
        "**Fidelización**",
        "**Mejor gestión de las horas punta**",
        "**Mejor gestión de retrasos**",
        "**Mejora de la experiencia del cliente**",
        "**Reducción del tiempo de espera**",
        "**Reducción de la cola visible**",
        "**Eficiencia de recursos**",
        "**Aumento de afluencia hacia las horas valle**",
        "**Mantenerse en contacto con los clientes**",
        "**Configurable de forma sencilla**",
        "**Personalizable (logo, empresa)**",
        "**Económico**",
        "**Compatible con tu gestión actual**"
      ],
      steps: [
        "El cliente recibe su turno o lo coge con su teléfono en 5 segundos o lo recibe por mail/SMS.",
        "Sigue la evolución de la cola en su teléfono y recibe notificaciones cuando se acerca su turno.",
        "Es avisado cuando le toca, con el lugar y la persona que le atenderá."
      ],
      video: {
        title: "Cómo funciona",
        description: "Mira cómo funciona smartQ en acción",
        videoId: "648fbfa0733849d2bdbcc5f2f9dbc9f5"
      },
      comparison: [
        { aspect: "Unirse", physical: "Hacer cola / ticket en papel", virtual: "Escanear QR / sitio web / añadido por empleado" },
        { aspect: "Actualizaciones", physical: "Preguntar al personal / mirar pantallas", virtual: "en el teléfono/notificaciones push en tiempo real" },
        { aspect: "Espera", physical: "Parece larga e incierta", virtual: "Transparente y con percepción reducida" },
        { aspect: "Ausencias", physical: "Más frecuentes, difíciles de gestionar", virtual: "Anulación y aplazamientos fáciles" },
        { aspect: "Operaciones", physical: "Llegadas impredecibles", virtual: "Llegadas previstas y conocidas de antemano" },
        { aspect: "Imagen de marca", physical: "Ticket genérico", virtual: "Ticket con su marca" }
      ],
      useCases: [
        "Comercios (atención al cliente)",
        "Peluquerías/Salones de belleza",
        "Salud",
        "Restaurantes/Foodtrucks",
        "Ventanillas",
        "Talleres/Reparaciones",
        "Salones/Eventos",
        "Recepción"
      ],
      indispensable: [
        {
          title: "Picos de afluencia esporádicos o retrasos",
          description: "Distribuya el flujo hacia las horas valle, elimine los cuellos de botella y mantenga un ritmo de servicio constante."
        },
        {
          title: "Duración de atención variable",
          description: "Reduzca los tiempos muertos entre intervenciones cortas y largas."
        },
        {
          title: "Renuncias y abandonos",
          description: "La espera virtual genera mayor aceptación."
        },
        {
          title: "Satisfacción cliente",
          description: "Respeta y valora su tiempo. Mejora experiencia, satisfacción y fidelización."
        },
        {
          title: "Multiples colas y prioridades (urgencias, triaje)",
          description: "Organice y priorice el orden de atención"
        },
        {
          title: "Recursos de personal variables",
          description: "Ajuste el ritmo de atención a la presencia real y los imprevistos del horario."
        },
        {
          title: "Lanzamientos y eventos con alto grado de incertidumbre",
          description: "Absorba las oleadas de demanda sin caos operativo."
        },
        {
          title: "Sala de espera reducida",
          description: "Desplace la espera fuera del local para mayor comodidad y seguridad."
        },
        {
          title: "Citas por varios canales (cita previa + sin cita + online)",
          description: "Organiza el orden de atención."
        },
        {
          title: "Gestión basada en la demanda actual",
          description: "Ajuste sus recursos según la demanda por servicio en tiempo real"
        }
      ],
      proof: [
        "Aumento de la satisfacción y la conversión con disminución de los abandonos.",
        "Información en tiempo real para optimizar equipos y flujos.",
        "Reducción de la espera real y percibida."
      ],
      objections: [
        { claim: "Va a complicar nuestra actividad.", reply: "Muy sencillo de uso, simplemente llamar 'Siguiente turno'." },
        { claim: "Mis clientes no quieren una aplicación.", reply: "No es una aplicación sino un simple QR a escanear. Sobre todo sus clientes no quieren perder su tiempo." },
        { claim: "¿Es compatible con mi sistema de gestión de citas?", reply: "Así es, además sus clientes tienen un número (digital/oral/en papel). Si ya tiene un sistema, sus clientes conocerán con precisión la hora exacta de atención." },
        { claim: "Mi negocio es muy pequeño.", reply: "Funciona por igual en negocios grandes y pequeños." }
      ],
      compliance: [
        "Informar claramente sobre el uso de los datos, recoger el consentimiento (SMS/correo electrónico), ofrecer una alternativa en papel para la equidad de acceso."
      ],
      implementation: [
        "Muestre un QR en la entrada, gestione desde un panel web/móvil, opción de pantalla (TV/tableta/teléfono) para mostrar quién es el siguiente."
      ],
      internalLinks: ["benefits", "features", "pricing"],
      visuals: [
        "Héroe: cliente con ticket digital en smartphone.",
        "Diagrama: cola física vs cola virtual (flujo comparado)."
      ],
      seo: { meta: "SmartQ transforma su cola en **cola virtual**: entrada por código QR, enlace web o SMS, seguimiento **en tiempo real**, **notificación** cuando sea su turno. Instalación sencilla, uso simple, **sin necesidad de hardware**. **Pruébelo gratis**" }
    },

    implementation: {
      title: "Instalación y soporte",
      purpose: "Quitar fricción al arranque: puesta en marcha rápida, con tus propios dispositivos, y soporte directo para que nada se pare.",
      hero: {
        headline: "¡Empieza ya mismo, tienes cuanto necesitas!",
        sub: "**Listo para empezar de inmediato · Sólo necesitas tu teléfono, tableta u ordinador · Fácil de usar desde el primer momento.**",
        ctaPrimary: "Prueba gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "**Configuración en minutos** - Empieza a gestionar colas en menos de 5 minutos.",
        "**Sin instalación compleja** - Solo necesitas un navegador web.",
        "**Sin conocimientos técnicos** - Interfaz intuitiva y fácil de usar.",
        "**Soporte 24/7** - Asistencia disponible en cualquier momento.",
        "**Actualizaciones automáticas** - Siempre tendrás la última versión sin hacer nada.",
        "**Seguridad garantizada** - Tus datos están protegidos y encriptados.",
        "**Acceso desde cualquier lugar** - Gestiona tus colas desde cualquier dispositivo.",
        "**Sin costes ocultos** - Todo incluido en tu plan."
      ],
      steps: [
        { title: "Paso 1 - Acceso", content: "Elige tu plan y te damos acceso al sistema." },
        { title: "Paso 2 - Configuración", content: "Modificas la configuración de la cola predeterminada con tus preferencias ." },
        { title: "Paso 3 - Personalización", content: "Pones tu marca (nombre, logotipo)." }
      ],
      useCases: [
        { title: "Escenario 1 - 1 sola cola", description: "Con tableta y teléfono móvil. Llamada a la siguiente persona desde tu teléfono y se muestra el número llamado en la pantalla de la tableta." },
        { title: "Escenario 2 - múltiples colas", description: "Con teléfono, ordenador y televisor. Llamada al siguiente desde el teléfono, se muestran las colas en el televisor y todo se administra desde el ordenador." }
      ],
      proof: [
        "\"Muy sencillo de configurar\" y ayuda remota cuando haga falta.",
        "Pantallas flexibles: un televisor, tablet o móvil valen para empezar.",
        "[Stat needed: \"Tiempo medio de implantación: X horas; formación: Y minutos.\"]"
      ],
      objections: [
        { claim: "¿Es difícil de poner en fucionamiento?", reply: "Por defecto viene con una cola ya configurada. Se puede cambiar la configuración y adaptar a tu negocio (nombre y logotipo)." },
        { claim: "¿Es difícil de usar?", reply: "Fácil de usar desde el 1er momento, simplemente hay que pulsar 'cliente siguiente'" },
        { claim: "¿Y si tengo alguna duda?", reply: "Documentación y soporte a su disposición (por mail y teléfono)." },
        { claim: "¿Qué material necesito?", reply: "Simplemente necesitas un teléfono (o una tableta o un ordenador)." }
      ],
      compliance: [
        "Formación básica en privacidad (consentimiento para SMS/e-mail), minimización de datos y opción ticket en papel para quienes no usan móvil."
      ],
      implementation: [
        "Tiempo de alta: rápido ([confirma promesa comercial: \"en 24 h\"]).",
        "Roles implicados: dueño/gerente y personal de mostrador.",
        "Integraciones/canales: QR + enlace, pantalla en TV/tablet/móvil, avisos SMS/e-mail/push (SMS con coste externo si se usa).",
        "Formación: en minutos gracias a una UI sencilla."
      ],
      internalLinks: ["features", "faqs", "pricing"],
      visuals: [
        "Héroe: foto de mostrador con QR visible y tablet mostrando \"próximo turno\".",
        "Gráfico: checklist \"Lanza hoy\" (crear colas → imprimir QR → mostrar en pantalla → atender)."
      ],
      seo: { meta: "Implanta smartQ rápido, con tus propios dispositivos y soporte directo. Menos colas, más servicio desde el primer día." }
    },

    benefits: {
      title: "Beneficios",
      purpose: "Las filas de espera tienen un impacto negativo en la experiencia y satisfacción del cliente.",
      hero: {
        headline: "Más clientes, menos estrés.",
        sub: "**Equilibra y Agiliza** la afluencia. **Gana** y **Fideliza** nuevos clientes",
        ctaPrimary: "Pruébalo gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        {
          category: "Ingresos",
          items: [
            "**Más ingresos**: atiendes a más clientes y recuperas ventas que antes se perdían.",
            "**Menos abandonos y más conversiones**: las colas visibles o mal gestionadas repelen a los clientes, la espera virtual es mejor aceptada.",
            "**Fidelización**: un cliente contento regresa de nuevo.",
            "**Menos ausencias sin aviso**: tus clientes pueden avisarte fácilmente. Si ocurre, llamas al siguiente de inmediato y minimizas el impacto.",
            "**Reducción de costos operativos**: menor tiempo de inactividad, menos espacio de espera y menor gestión de llegadas.",
            "**Ventaja competitiva**: mejor servicio"
          ]
        },
        {
          category: "Mayor productividad",
          items: [
            "**Mejor gestión de picos de afluencia y retrasos**: absorción sin degradación del servicio y protegiendo tu equipo del estrés. Menos abandonos, maximizando los clientes.",
            "**Afluencia mejor distribuida**: en función del tiempo de espera real, tus clientes pueden posponer su llegada.",
            "**Menos tiempos muertos**: distribución de manera continua y uniforme. Sin clientes, puedes liberarte hasta que se apunte el próximo.",
            "**Fluidez**: servicio más ágil con menor espera por cliente.",
             "**Gestion de espera automatizada**: liberas tu equipo del peso de gestionar clientes impacientes, gestionar conflictos o deber dar explicaciones.",
            "**Reducción de cuellos de botella**: permite replanificar y adaptarse",
            "**Optimización de espacios físicos**: reducción de la saturación en la sala de espera",
            "**Maximizar el uso de recursos**: visibilidad global para asignar recursos dinámicamente de manera eficiente"
          ]
        },
        {
          category: "Mejor experiencia cliente",
          items: [
            "**Mejora del servicio**: focalizado en el cliente actual",
            "**Personal con menos estrés**: menos tensión al aumentar la espera y mejor interacción con los clientes. ",
            "**Valoración positiva**: además de mejorar el servicio, tus clientes valorarán que respetes su tiempo",
            "**Conexión con tus clientes**: datos de contacto (con su consentimiento)"
          ]
        }
      ],
      steps: [
        "El cliente ve el tiempo estimado y se une por QR o enlace (sin app).",
        "smartQ envía actualizaciones y recordatorios; el cliente puede posponer o cancelar si lo necesita.",
        "El personal atiende en orden y con visibilidad total del flujo."
      ],
      useCases: [
        "Retail (retiros, atención, devoluciones) con filas separadas por servicio.",
        "Salud (consultas) con menos congestión y avisos \"justo a tiempo\".",
        "Servicios públicos y transporte (re-emisión/ticketing) más ordenados."
      ],
      proof: [
        "La información de la fila muestra el tiempo de espera por servicio y permite optimizar la atención.",
        "La espera virtual reduce la insatisfacción y mejora la fidelización.",
        "40% menos de abandonos. Los clientes se quedan cuando pueden gestionar los tiempos de espera."
      ],
      objections: [
        { claim: "Una cola tradicional es suficiente.", reply: "Te mejoramos la gestión, reducimos el estrés y eliminamos la impaciencia y desinformación." },
        { claim: "Nuestra demanda es impredecible.", reply: "Información del número de personas para que puedas anticipar y organizarte mejor." },
        { claim: "La gente no quiere compartir sus datos.", reply: "No existe obligación de comunicar su información" }
      ],
      compliance: [
        "Transparencia sobre uso de datos, consentimiento para SMS/e-mail y alternativa ticket en papel para accesibilidad y equidad. Publicar reglas de prioridad si existen."
      ],
      implementation: [
        "Tiempo de alta: rápido. Roles: dueño/manager + front-desk. Integraciones: canales SMS/e-mail/push. Formación: minutos con panel intuitivo (web/móvil)."
      ],
      internalLinks: ["journey", "features", "pricing"],
      visuals: [
        "Gráfico \"antes/después\" de abandono de fila.",
        "Heatmap de horas pico + diagrama \"fila física vs Virtual queue\"."
      ],
      seo: { meta: "Beneficios de la Virtual queue: menos abandono, mejor experiencia y decisiones con datos — simple de implementar." }
    },

    features: {
      title: "Producto: smartQ — Funcionalidades",
      purpose: "Mostrar herramientas concretas, fáciles y útiles desde el primer día, sin jerga técnica.",
      hero: {
        headline: "¡Tu móvil espera por ti!",
        sub: "Ya no esperas de más. **Completo y muy fácil** de usar. ",
        ctaPrimary: "Pruébalo gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "Cola en tiempo real. Seguimiento de la cola en cualquier lugar con actualizaciónes y avisos.",
        "Notificaciones push. Alertas automáticas cuando se acerca el turno para mantener informados a los clientes (Android).",
        "Acceso multicanal. Coger turno por código QR, enlace, correo electrónico, SMS o en papel.",
        "Alertas. Avisos en caso de incidencias.",
        "Llamada de turnos. Llamada en tu teléfono, por pantalla y recepción de notificación push.",
        "Segmentación por servicio. Distribución de clientes en diferentes colas según el tipo de servicio ofrecido.",
        "Apertura/Cierre. Apertura y cierre de colas.",
        "Pre-entrada. Opción de empezar a esperar en la cola antes de llegar al establecimiento.",
        "Aplazamiento o anulación. Posibilidad de posponer o anular si el turno no es inminente.",
        "Información de turnos en pantalla. Llamadas de turno con sonido y visualización en pantalla de tv, ordenador, tableta o teléfono.",
        "Marca personalizada. Nombre, información y logotipo de tu negocio, incluso en correos electrónicos y SMS.",
        "Estadísticas. Seguimiento y análisis de datos de colas, monitoreo de tiempos de espera e identificación de cuellos de botella.",
        "Datos del cliente. Recopilación de información para mantenerte en contacto.",
        "Configurable. Personalización para adaptar las funcionalidades a tu negocio.",
        "Panel de control web y móvil. Gestión y configuración de colas desde una interfaz intuitiva accesible desde cualquier dispositivo.",
        "Multilingüe. Disponible en francés, inglés, español e italiano. Posibilidad de añadir más idiomas."
      ],
      steps: [
        {
          title: "",
          subtitle: "Mira cómo funciona smartQ en acción",
          content: "LOOM_VIDEO_648fbfa0733849d2bdbcc5f2f9dbc9f5"
        },
        {
          title: "Paso 1 - Apertura",
          subtitle: "Apertura de la cola, permitiendo la toma de turnos (en el local o de forma remota)",
          content: ""
        },
        {
          title: "Paso 2 - Siguiente turno",
          subtitle: "Llamada al siguiente cliente. Posibilidad de actualizar el tiempo de espera estimado.",
          content: ""
        },
        {
          title: "Paso 3 - Cierre",
          subtitle: "Cierre de la cola, ya no se pueden coger más turnos",
          content: ""
        }
      ],
      useCases: [
        "Únete con antelación de forma remota.",
        "Envía por correo electrónico o SMS su posición a tus clientes.",
        "Informa a tus clientes, con o sin cita previa, de su tiempo de espera real, especialmente en caso de retrasos.",
        "Avisa en caso de incidencias.",
        "Mostrar las llamadas en pantalla (TV, tableta, teléfono u ordenador)",
        "Posponer o cancelar si la espera aún es larga"
      ],
      proof: [
        "Los informados tienen información actualizada en todo momento.",
        "Productividad y mejora del servicio",
        "Horas punta y retrasos sin estrés."
      ],
      objections: [
        { claim: "¿Podrán anular?", reply: "Opcional. Puede permitir la anulación o aplazamiento cuando la espera es aún larga." },
        { claim: "¿Puedo añadir el nombre de mi marca?", reply: "Puede añadir el nombre de su marca y su logotipo." },
        { claim: "¿Y si hay algún problema?", reply: "Soporte disponible por correo electrónico y teléfono en caso necesario." }
      ],
      compliance: [
        "Alternativa de ticket en papel para quienes no quieran compartir sus datos; opción de exclusión sencilla."
      ],
      implementation: [
        "Instalar, crear colas, mostrar el código QR y comenzar a atender, todo desde un panel de control sencillo (móvil y web)."
      ],
      internalLinks: ["journey", "implementation"],
      visuals: [
        "Maqueta móvil (ticket + estado en vivo), ejemplos del panel de control, ejemplos de notificaciones."
      ],
      seo: { meta: "Sistema de Gestión de Colas Sencillo y Fácil: actualizaciones, notificaciones, estadísticas, personalización de marca y administración intuitiva." }
    },

    journey: {
      title: "Recorrido del usuario",
      purpose: "Hacer tangible la experiencia con smartQ: unirse en segundos, esperar con libertad y presentarse justo a tiempo — sin colas físicas.",
      hero: {
        headline: "Coge tu turno y ¡no esperes de más!",
        sub: "La solución a la **impaciencia**, los **imprevistos** y el **abandono**.",
        ctaPrimary: "Prueba gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "**Fácil e instantáneo**: coge turno en 5 segundos, sin app ni complicaciones.",
        "**Información en directo**: elimina la incertidumbre y la impaciencia estando informado de su situación en todo momento",
        "**Tiempo de espera útil**: permite emplear su tiempo de forma productiva sin preocupaciones.",
        "**Avisos**: notificaciones automáticas cuando se acerca su turno (Android)."
      ],
      steps: [
      "Coge turno escaneando un QR y recibe tu posición al momento.",
        "Sigue tu posición en tiempo real. Recibes avisos cuando se acerca tu turno.",
        "Eres avisado en pantalla y en tu teléfono cuando es tu turno."
      ],
      useCases: [
        "Barberías y salones: esperas sin gente de pie en la puerta.",
        "Clínicas: salas menos abarrotadas y llegadas justo a tiempo.",
        "Atención al cliente/retail: devoluciones y postventa con colas separadas.",
        "Servicios públicos y transporte: turnos claros y ordenados."
      ],
      proof: [
        "El cliente ve su puesto y la espera estimada; baja la ansiedad y mejora la experiencia.",
        "[Stat needed: \"Tiempo de espera percibido ↓ X%; tasa de abandono ↓ Y% tras 4 semanas.\"]"
      ],
      objections: [
        
        { claim: "¿Y si alguien no tiene smartphone?", reply: "Se les informa de su posición y siguen su posición en pantalla." },
        { claim: "¿ Cómo puedo gestionar los retrasos/imprevistos ?", reply: "Puedes enviar un SMS para que sepan cuando serán realmente atendidos" },
        { claim: "¿Se les puede pasar el turno ?", reply: "Son notificados cuando se acerca su turno. " },
        
      ],
      compliance: [
        "Cartelería clara y de alto contraste; alternativa no digital (ticket en papel); consentimiento informado para notificaciones y respeto a la privacidad."
      ],
      implementation: [
        "Tiempo de despliegue: rápido. Solo imprime el QR, colócalo en la entrada y, si quieres, muestra \"quién va\" en TV/tablet/móvil. Panel web/móvil para el equipo."
      ],
      internalLinks: ["features", "implementation", "pricing"],
      visuals: [
        "Héroe: cliente con ticket digital en el móvil mostrando posición en la cola.",
        "Diagrama: \"fila física vs Virtual queue\" (entrada → espera libre → aviso → atención)."
      ],
      seo: { meta: "Recorrido del visitante con smartQ: únete por QR, sigue tu turno y vuelve con aviso — esperar sin hacer cola." }
    },

    faqs: {
      title: "FAQs",
      purpose: "Resolver de forma directa las dudas típicas de dirección, operaciones y mostrador para acelerar la decisión y la implantación.",
      hero: {
        headline: "Resolvemos tus dudas.",
        sub: "Sin obligación de app, código QR o link, prueba gratuita.",
        ctaPrimary: "Iniciar prueba gratuita"
      },
      benefits: [
        "Unirse en sitio o a distancia. Desde smartphone, web o kiosco. Sin app obligatoria.",
        "Alternativa sin móvil. Ticket en papel y seguimiento en pantalla.",
        "Sin permanencia. Suscripción mensual y sin penalización por baja."
      ],
      steps: [
        "El cliente escanea un QR o abre un enlace y se añade a la cola.",
        "Recibe actualizaciones en tiempo real con su posición y tiempo estimado.",
        "smartQ le avisa cuando se acerca su turno."
      ],
      useCases: [
        "Transporte (re-emisión/ticketing)",
        "Retail (recogidas, postventa, devoluciones)",
        "Salud",
        "Servicios públicos"
      ],
      proof: [
        "Prueba gratuita de 1 mes (auto-configuración).",
        "Push gratuito; SMS ~0,08 € vía servicio de pruebas Twilio durante el trial (cuenta disponible).",
        "[Stat needed: \"Satisfacción ↑ X pts tras 4 semanas.\"]"
      ],
      objections: [
        { claim: "¿El QR es obligatorio?", reply: "No. Puedes entregar un ticket impreso o manuscrito y mostrar el turno en pantalla." },
        { claim: "¿Es complicado de configurar?", reply: "Muy sencillo; interfaz clara y ayuda remota si la necesitas." },
        { claim: "¿Hay compromiso o costes ocultos?", reply: "Sin contrato y sin costes de cancelación." }
      ],
      compliance: [
        "Mostrar política de privacidad al unirse; recoger consentimiento para SMS/e-mail; minimización de datos y derecho a borrado; alternativa no digital (ticket)."
      ],
      implementation: [
        "Auto-alta posible; personal formado en minutos; canales flexibles (push/e-mail/SMS). Soporte directo del equipo técnico."
      ],
      internalLinks: ["pricing", "implementation"],
      visuals: [
        "Cards de FAQ; iconos \"formas de unirse\" (QR, enlace, kiosco, papel)."
      ],
      seo: { meta: "FAQ smartQ: sin app, opción ticket en papel, prueba gratis y sin permanencia. Respuestas claras a las dudas más habituales." }
    },

    'use-cases': {
      title: "Sectores y casos de uso",
      purpose: "Demostrar dónde smartQ aporta valor real en el día a día: menos colas, más ventas y una atención más ágil en retail, salud, servicios públicos, transporte y eventos.",
      hero: {
        headline: "Un sistema sencillo. Resultados en cualquier sector.",
        sub: "Retail, clínicas, administraciones y transporte: esperar sin hacer cola para clientes, control total para tu equipo.",
        ctaPrimary: "Prueba gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "Retail: Acelera recogidas y devoluciones, separa colas por servicio y libera tu mostrador.",
        "Salud: Salas menos saturadas y llegadas \"justo a tiempo\" con avisos automáticos.",
        "Servicios públicos: Turnos transparentes y ordenados (primero en llegar, primero en ser atendido).",
        "Transporte/Eventos: Reemisiones y accesos fluidos, sin aglomeraciones en picos."
      ],
      steps: [
        "Publica QR y enlace en local, web y redes.",
        "Los clientes se unen y reciben tiempos estimados y avisos.",
        "Tu equipo atiende por orden, con colas separadas si lo necesitas."
      ],
      useCases: [
        "Tienda/posventa: recogidas, garantías y devoluciones en flujos distintos.",
        "Clínica/consulta: triage simple, llamadas por turno y menos espera presencial.",
        "Ayuntamiento/ventanilla única: trámites por colas (licencias, padrón, pagos).",
        "Taquillas/transporte/eventos: accesos escalonados y comunicación \"casi te toca\"."
      ],
      proof: [
        "La segmentación de colas reduce cuellos de botella y hace predecible la espera.",
        "La visibilidad en tiempo real disminuye la ansiedad del cliente.",
        "[Stat needed: \"Abandono ↓ X%; throughput (clientes/hora) ↑ Y%.\"]"
      ],
      objections: [
        { claim: "Tenemos muchos servicios distintos.", reply: "Crea colas por servicio y asigna prioridades cuando proceda." },
        { claim: "Nuestra sede es pequeña.", reply: "Arranca con tu móvil y una TV; no necesitas hardware especial." },
        { claim: "¿Y si los clientes no quieren dar datos?", reply: "Opción ticket en papel y token anónimo." },
        { claim: "¿Qué sectores?", reply: "Transporte, retail, salud, servicios públicos - cualquier lugar donde haya colas." }
      ],
      compliance: [
        "Cartelería clara y accesible; consentimiento para SMS/e-mail; minimización de datos; alternativa no digital (ticket) para garantizar la equidad."
      ],
      implementation: [
        "Tiempo de puesta en marcha: rápido.",
        "Roles: gerente + personal de mostrador.",
        "Integraciones/canales: QR y enlace; avisos push/e-mail/SMS (el SMS puede tener coste del proveedor).",
        "Formación: minutos con un panel intuitivo."
      ],
      internalLinks: ["features", "journey", "pricing"],
      visuals: [
        "Héroe: mosaico de sectores (retail, clínica, ventanilla, transporte) con titulares cortos.",
        "Diagrama: \"segmentación de colas por servicio\" (p. ej., devoluciones / recogidas / atención técnica)."
      ],
      seo: { meta: "Virtual queue para retail, salud, administraciones y transporte: segmenta colas, reduce esperas y mejora el flujo sin hardware especial." }
    },

    pricing: {
      title: "Precios",
      purpose: "Hacer que la decisión sea sencilla: prueba gratis, alta inmediata y planes claros que cubren lo esencial desde el primer día.",
      hero: {
        headline: "Transforma la espera en una ventaja competitiva",
        sub: "**La cola rentable. Prueba gratis, sin compromiso.**",
        ctaPrimary: "Prueba gratis",
        ctaSecondary: "Reservar una demo"
      },
      benefits: [
        "**Configuración simple y rápida**: puesta en marcha inmediata y fácil de configurar.",
        "**Fácil adopción**: uso diario sencillo, interfaz intuitiva.",
        "**Gestión sencilla**: panel de control intuitivo. No se requieren conocimientos técnicos.",
        "**Personalizable**: activa o desactiva funciones según tus preferencias.",
        "**Sin necesidad de hardware**: puedes empezar con tus dispositivos actuales.",
        "**Branding**: personalízalo con tu marca.",
        "**Prueba gratuita**: pruébalo sin compromiso durante 30 días.",
        "**Sin compromiso de permanencia**: suscripción mensual, cancela cuando quieras.",
        "**Precio asequible**.",
        "**Soporte al cliente**: asistencia para ayudarte en todo momento."
      ],
      steps: [
        "Elige tu plan y activa la prueba gratuita (1 mes).",
        "Configura tu cola y tu branding",
        "Comienza a operar "
      ],
      proof: [
        "Sin datos de pago para la prueba, alta inmediata y sin contrato.",
        "Solo Site — $19.99 / £14.99 al mes. Incluye: Unlimited Walk-ins, Full Queue Optimisation, Queue Analytics, QR Queue Generator, WhatsApp Integration, Unlimited Staff, Business Support.",
        "Multi-Site — precio bajo solicitud.",
        "Custom App — precio bajo solicitud (para cadenas / gran escala).",
        "Push sin coste adicional; SMS ~0,08 € a través de Twilio en el modo de pruebas (durante el trial puedes usar su cuenta sin coste).",
        "Opción auto-configuración citada a 10 €/mes (alinear mensaje final con la página de precios).",
        "[Stat needed: \"Payback típico en X semanas; adopción ↑ Y% en el primer mes.\"]"
      ],
      objections: [
        { claim: "¿Hay permanencias o letra pequeña?", reply: "No. Suscripción mensual, cancelación sin coste." },
                { claim: "¿Qué incluye el plan básico?", reply: "Todo lo esencial para operar." },
        { claim: "¿Podemos adaptarlo?", reply: "Muchas opciones disponibles en la aplicación, con personalización adicional bajo petición." },
        { claim: "¿Cuál es el coste de enviar SMS?", reply: "Notificaciones push gratis, SMS se paga al por proveedor (Twilio) según sus tarifas." },
      ],
      compliance: [
        "Transparencia de precios y costes de terceros (SMS), condiciones de baja claras y privacidad/GDPR en comunicaciones (consentimiento, opt-out)."
      ],
      implementation: [
        "Prueba gratis 1 mes y auto-setup posible; soporte del equipo técnico si lo necesitas."
      ],
      internalLinks: ["features", "faqs"],
      visuals: [
        "Héroe: tabla de planes clara con checkmarks.",
        "Nota visual: tarjeta \"Coste de SMS\" con recordatorio de push gratis."
      ],
      seo: { meta: "Precios claros de Virtual queue: prueba gratis, sin contrato y alta inmediata. Del plan Solo a Custom, paga solo por lo que usas." }
    }
  },
  checklist: [],
  openQuestions: []
}

// Navigation items for Spanish
export const navigationEs = {
  main: [
    { name: 'Beneficios', href: '/benefits' },
    { name: 'Funcionalidades', href: '/features' },
    { name: 'Utilización', href: '/journey' },
    { name: 'Instalación', href: '/implementation' },
    { name: 'Precios', href: '/pricing' },
    { name: 'FAQ', href: '/faqs' },
  ],
  cta: [
    { name: 'Prueba gratis', href: '/es/signup', primary: true },
    { name: 'Demostración', href: '/es/demo', primary: false },
  ],
  footer: [
    { name: 'Privacidad', href: '/privacy' },
    { name: 'Términos', href: '/terms' },
    { name: 'Contacto', href: 'mailto:contact@smartq.prod' },
  ]
}

// Spanish site configuration
export const siteConfigEs = {
  name: 'smartQ',
  description: 'Gestión de Cola Virtual - La mejor forma de hacer cola',
  url: 'https://smartq.pro',
  ogImage: 'https://smartq.pro/social/og.jpg',
  links: {
    twitter: 'https://twitter.com/smartqueue',
    github: 'https://github.com/smartqueue',
  },
}

// Spanish footer sections
export const footerSectionsEs = {
  product: 'Producto',
  company: 'Empresa',
  legal: 'Legal'
}

// Helper function to process markdown-like bold text and line breaks
export function processMarkdownEs(text: string): string {
  // Skip processing if it's a Loom video placeholder
  if (text.startsWith('LOOM_VIDEO_')) {
    return text;
  }
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}
